import { GetMainPhoto } from './GetMainPhoto.js';
import { CalcLeftRight } from './CalcLeftRight.js';
import { GoogleGeometry } from './GoogleGeometry.js';
import { CalcScenarioSagMove } from './CalcScenarioSagMove.js';

//
// This function can be used to apply a marker's effective moves to the given height (in inches)
// This function also takes two object parameters for additional data
//
// computeData: This object stores data that will be used to calculate the adjusted height. It can contain the following properties:
//     ratios: an object containing the keys for the main photos of each endpoint of the connection. The value for each is the ratio of total distance from the marker's section that endpoint
//
// --- If you don't supply the above ratio parameter, then this function can calculate it by you supplying the following parameters:
//
//     connectionId: the id of the connection that the marker is found in
//     sectionId: the id of the section that the marker is found in
//     nodes: the nodes object containing the nodes in the job
//     connections: the connections object containing the connections in the job
//
// options: Object containing optional data
//     returnEffectiveMoveAmount: (true or false, defaults to false) set to true to return the net move amount of the effective moves, instead of the adjusted height

export function ApplyEffectiveMoves(marker, markerBaseHeight, computeData, options) {
  options = options || {};
  computeData = computeData || {};
  var conductor = { converges: false }; //only assigned if computeData.worstCaseSag is given

  let effectiveMoveAmount = 0;

  if (marker && marker._effective_moves) {
    if (!computeData.ratios) {
      computeData.ratios = CalcPhotoDistanceRatios(
        computeData.connectionId,
        computeData.sectionId,
        computeData.nodes,
        computeData.connections
      );
    }

    if (computeData.ratios) {
      if (computeData.worstCaseSag) {
        conductor = {
          windVelocity: computeData.windVelocity, // velocity of wind in worst case condtions (mph)
          collectionTemp: computeData.collectionTemp, // temperate at time of field survey (°F)
          nescWindLoadFactor: computeData.nescWindLoadFactor,

          attachmentHeight1: computeData.attachmentHeight1,
          attachmentHeight2: computeData.attachmentHeight2,
          effectiveMoveAttachmentHeight1: computeData.effectiveMoveAttachmentHeight1, // mr_move on left pole
          effectiveMoveAttachmentHeight2: computeData.effectiveMoveAttachmentHeight2, // mr_move on right pole
          midspanHeight: computeData.midspanHeight, // height of conductor in midspan photo (ft)

          elevationSupport1: computeData.elevationSupport1, //397, // elevation of ground at left pole (ft)
          elevationSupport2: computeData.elevationSupport2, //403, // elevation of ground at right pole (ft)
          elevationMidspan: computeData.elevationMidspan, //400, // elevation of ground at midspan (ft)
          spanLength: computeData.spanLength, // distance between left and right poles (ft)
          sectionRatio: computeData.sectionRatio, // ratio of section position in span from left pole

          weight: computeData.weight, // linear weight of conductor (lb/ft)
          diameter: computeData.diameter, // smallest diameter encompassing entire conductor bundle (in)
          crossSectionalArea: computeData.crossSectionalArea, // cross-sectional area of conductor/strand (sq in)
          expansionCoefficient: computeData.expansionCoefficient, // expansion coefficient of conductor/strand (1/°F)
          modulusOfElasticity: computeData.modulusOfElasticity, // modulus of elasticity of conductor/strand (lbs/sq in)
          mcot: computeData.mcot, // maximum conductor operating temperature (°F)
          converges: true,

          get adjustedSupportHeight1() {
            return calcAdjustedHeight(this.attachmentHeight1, this.elevationSupport1);
          }, // height of conductor attachment at left pole adjusted for elevation (ft)
          get adjustedSupportHeight2() {
            return calcAdjustedHeight(this.attachmentHeight2, this.elevationSupport2);
          }, // height of conductor attachment at right pole adjusted for elevation (ft)
          get adjustedMidspanHeight() {
            return calcAdjustedHeight(this.midspanHeight, this.elevationMidspan);
          }, // height of conductor in midspan photo adjusted for elevation (ft)
          get conductorScenarios() {
            return setScenarios(this.mcot);
          }, // set the specific scenarios based on the conductor properties
          get midspanDistance() {
            return calcSectionDistance(this.spanLength, this.sectionRatio);
          }, // distance from left pole to midspan photo (ft)
          get initialCatenaryConstant() {
            let icc = calcInitialCatenaryConstant(
              this.spanLength,
              this.midspanDistance,
              this.adjustedSupportHeight1,
              this.adjustedSupportHeight2,
              this.adjustedMidspanHeight
            );
            if (!icc.converge) this.converges = false;
            return icc.constant;
          }, // catenary constant of conductor at initial conditions
          get initialHorizontalTension() {
            return calcHorizontalTension(this.initialCatenaryConstant, this.weight);
          }, // horizontal tension of conductor at initial conditions (lbs)
          get initialConductorLength() {
            return calcConductorLength(
              this.initialCatenaryConstant,
              this.spanLength,
              this.adjustedSupportHeight1,
              this.adjustedSupportHeight2
            );
          }, // stressed conductor length at initial conditions (ft)
          get initialUnstressedConductorLength() {
            return calcInitialUnstressedLength(
              this.initialConductorLength,
              this.initialHorizontalTension,
              this.crossSectionalArea,
              this.modulusOfElasticity
            );
          }, // unstressed conductor length at initial conditions (ft)
          get iceWeight() {
            return calcIceWeight(this.diameter, this.conductorScenarios);
          }, // linear weights of the ice on the conductor at final conditions (lbs/ft)
          get windLoad() {
            return calcWindLoad(this.diameter, this.conductorScenarios, this.windVelocity);
          }, // weights of the wind acting perpendicular to the conductor under different scenarios (lbs)
          get finalWeight() {
            return calcFinalCableWeight(this.weight, this.iceWeight, this.windLoad, this.nescWindLoadFactor);
          }, // linear weights of the conductor at final conditions (lbs/ft)
          get finalUnstressedConductorLength() {
            return calcFinalUnstressedLength(
              this.initialUnstressedConductorLength,
              this.expansionCoefficient,
              this.conductorScenarios,
              this.collectionTemp
            );
          }, // unstressed conductor length at final temperatures (ft)
          get finalCatenaryConstant() {
            let fcc = calcFinalCatenaryConstant(
              this.spanLength,
              this.adjustedSupportHeight1,
              this.adjustedSupportHeight2,
              this.finalWeight,
              this.crossSectionalArea,
              this.modulusOfElasticity,
              this.finalUnstressedConductorLength
            );
            if (!fcc.converge) this.converges = false;
            return fcc.constants;
          }, // catenary constants of conductor at final conditions
          get finalConductorLowPointsY() {
            return calcCatenaryLowPointsY(
              this.finalCatenaryConstant,
              this.spanLength,
              this.adjustedSupportHeight1,
              this.adjustedSupportHeight2
            );
          }, // heights of conductor low point under different scenarios (ft)
          get finalConductorLowPointY() {
            return calcFinalConductorLowPoint(this.finalConductorLowPointsY, this.elevationMidspan);
          }, // height of conductor low point (ft)
          get finalConductorHeightAtMidspan() {
            return calcCatenaryPosition(
              this.finalCatenaryConstant,
              this.spanLength,
              this.midspanDistance,
              this.adjustedSupportHeight1,
              this.adjustedSupportHeight2,
              this.elevationMidspan,
              this.effectiveMoveAttachmentHeight1,
              this.effectiveMoveAttachmentHeight2
            );
          } // height of conductor at midspan
        };
      }

      //If data converges, use this worst case sag.  Else, continue to fallback code
      if (conductor.converges) {
        effectiveMoveAmount = (conductor.finalConductorHeightAtMidspan - conductor.midspanHeight) * 12;
      } else {
        for (let photoId in marker._effective_moves) {
          //for each pole attached to the marker
          if (computeData.ratios[photoId] && isNaN(marker._effective_moves[photoId]) == false) {
            effectiveMoveAmount += parseFloat(marker._effective_moves[photoId]) * computeData.ratios[photoId]; //effective move = pole * ratio
          }
        }
      }
    }
  }

  // Move the marker to where the wire would hang under a weather change
  const {
    move: scenarioSagMove,
    error: scenarioSagError,
    details: scenarioSagDetails
  } = computeData.scenarioSag ? CalcScenarioSagMove({ marker, ...computeData.scenarioSag }) : { move: 0, error: null, details: null };
  effectiveMoveAmount += scenarioSagMove;

  const sagAdjustment = Number(marker?.sag_adjustment);
  const additionalManualAdjustment = isNaN(sagAdjustment) ? 0 : sagAdjustment;

  // Round the sum of the effective moves
  effectiveMoveAmount = Math.round(effectiveMoveAmount + additionalManualAdjustment);

  // // Check if we should return the moved amount or the final height
  // if(options.returnEffectiveMoveAmount == true) {
  //     return effectiveMoveAmount;
  // }
  // return markerBaseHeight + effectiveMoveAmount;

  return {
    effectiveMoveAmount: effectiveMoveAmount,
    effectiveMoveWithoutManualAdjustment: effectiveMoveAmount - additionalManualAdjustment,
    additionalManualAdjustment,
    finalHeight: markerBaseHeight + effectiveMoveAmount,
    wcsConverge: conductor.converges,
    scenarioSagMove,
    scenarioSagError,
    scenarioSagDetails
  };
}

/*
	Psuedo code for how script should run:
	1. For each span, loop through all sections
	2. For each section, loop through all wires
	3. For each wire, loop through all loading_analysis attributes
	4. For each loading_analysis, return conductor.finalConductorHeightAtMidspan()
	5. Apply lowest result as effective move for that wire in that midspan
*/

/*  
    these should be hard coded if loading_case attribute contains "NESC"
*/

function calcAdjustedHeight(height, elevation) {
  return height + elevation;
}

function setScenarios(mcot) {
  let conductorScenarios = [
    {
      iceThickness: 0,
      finalTemperature: 120
    },
    {
      iceThickness: 0.5,
      finalTemperature: 32
    }
  ];
  if (mcot !== null) {
    if (mcot > conductorScenarios[0].finalTemperature) {
      conductorScenarios[0].finalTemperature = mcot;
    }
  }
  return conductorScenarios;
}

function calcSectionDistance(spanLength, sectionRatio) {
  return spanLength * sectionRatio;
}

function calcConductorLength(catenaryConstant, spanLength, adjustedSupportHeight1, adjustedSupportHeight2) {
  // actual length of the conductor in the initial conditions
  return Math.sqrt(
    4 * Math.pow(catenaryConstant, 2) * Math.pow(Math.sinh(spanLength / (2 * catenaryConstant)), 2) +
      Math.pow(adjustedSupportHeight2 - adjustedSupportHeight1, 2)
  );
}

function calcInitialUnstressedLength(stressedConductorLength, horizontalTension, crossSectionalArea, modulusOfElasticity) {
  return stressedConductorLength / (1 + horizontalTension / (crossSectionalArea * modulusOfElasticity));
}

function calcFinalUnstressedLength(initialUnstressedLength, expansionCoefficient, scenarios, initialTemp) {
  let lengths = [];
  for (let i = 0; i < scenarios.length; i++) {
    lengths[i] = initialUnstressedLength * (1 + expansionCoefficient * (scenarios[i].finalTemperature - initialTemp));
  }
  return lengths;
}

function calcIceWeight(diameter, scenarios) {
  let weights = [];
  for (let i = 0; i < scenarios.length; i++) {
    weights[i] = 1.244 * (diameter * scenarios[i].iceThickness + Math.pow(scenarios[i].iceThickness, 2));
  }
  return weights;
}

function calcHorizontalWindPressure(windVelocity) {
  return 0.22056 * Math.pow(windVelocity, 2);
}

function calcWindLoad(diameter, scenarios, windVelocity) {
  let windLoads = [];
  for (let i = 0; i < scenarios.length; i++) {
    windLoads[i] = (calcHorizontalWindPressure(windVelocity) * (diameter + 2 * scenarios[i].iceThickness)) / 12;
  }
  return windLoads;
}

function calcFinalCableWeight(initialWeight, iceWeight, windLoad, windLoadFactor) {
  let finalWeights = [];
  for (let i = 0; i < iceWeight.length; i++) {
    finalWeights[i] = Math.sqrt(Math.pow(initialWeight + iceWeight[i], 2) + Math.pow(windLoad[i], 2)) + windLoadFactor;
  }
  return finalWeights;
}

function calcCatenaryPosition(
  catenaryConstant,
  spanLength,
  xDistance,
  adjustedSupportHeight1,
  adjustedSupportHeight2,
  elevationMidspan,
  effectiveMoveAttachmentHeight1,
  effectiveMoveAttachmentHeight2
) {
  let finalPosition;
  if (effectiveMoveAttachmentHeight1 != null) adjustedSupportHeight1 += effectiveMoveAttachmentHeight1;
  if (effectiveMoveAttachmentHeight2 != null) adjustedSupportHeight2 += effectiveMoveAttachmentHeight2;
  if (typeof catenaryConstant === 'object') {
    let finalPositions = [];
    for (let i = 0; i < catenaryConstant.length; i++) {
      finalPositions[i] =
        2 *
          catenaryConstant[i] *
          (Math.pow(
            Math.sinh(
              spanLength / (4 * catenaryConstant[i]) -
                xDistance / (2 * catenaryConstant[i]) -
                (1 / 2) *
                  Math.asinh(
                    (adjustedSupportHeight2 - adjustedSupportHeight1) /
                      (2 * catenaryConstant[i] * Math.sinh(spanLength / (2 * catenaryConstant[i])))
                  )
            ),
            2
          ) -
            Math.pow(
              Math.sinh(
                spanLength / (4 * catenaryConstant[i]) -
                  (1 / 2) *
                    Math.asinh(
                      (adjustedSupportHeight2 - adjustedSupportHeight1) /
                        (2 * catenaryConstant[i] * Math.sinh(spanLength / (2 * catenaryConstant[i])))
                    )
              ),
              2
            )) +
        adjustedSupportHeight1 -
        elevationMidspan;
    }
    finalPosition = calcLowestArrayItem(finalPositions);
  } else {
    finalPosition =
      2 *
        catenaryConstant *
        (Math.pow(
          Math.sinh(
            spanLength / (4 * catenaryConstant) -
              xDistance / (2 * catenaryConstant) -
              (1 / 2) *
                Math.asinh(
                  (adjustedSupportHeight2 - adjustedSupportHeight1) /
                    (2 * catenaryConstant * Math.sinh(spanLength / (2 * catenaryConstant)))
                )
          ),
          2
        ) -
          Math.pow(
            Math.sinh(
              spanLength / (4 * catenaryConstant) -
                (1 / 2) *
                  Math.asinh(
                    (adjustedSupportHeight2 - adjustedSupportHeight1) /
                      (2 * catenaryConstant * Math.sinh(spanLength / (2 * catenaryConstant)))
                  )
            ),
            2
          )) +
      adjustedSupportHeight1;
  }
  return finalPosition;
}

function calcCatenaryLowPointsY(catenaryConstant, spanLength, adjustedSupportHeight1, adjustedSupportHeight2) {
  let lowPoint;
  if (typeof catenaryConstant === 'object') {
    let lowPoints = [];
    for (let i = 0; i < catenaryConstant.length; i++) {
      lowPoints[i] =
        adjustedSupportHeight1 -
        2 *
          catenaryConstant[i] *
          Math.pow(
            Math.sinh(
              spanLength / (4 * catenaryConstant[i]) -
                (1 / 2) *
                  Math.asinh(
                    (adjustedSupportHeight2 - adjustedSupportHeight1) /
                      (2 * catenaryConstant[i] * Math.sinh(spanLength / (2 * catenaryConstant[i])))
                  )
            ),
            2
          );
    }
    lowPoint = lowPoints;
  }
  /* c8 ignore start */
  // This else block is never reached because catenaryConstant is always an object
  else {
    lowPoint =
      adjustedSupportHeight1 -
      2 *
        catenaryConstant *
        Math.pow(
          Math.sinh(
            spanLength / (4 * catenaryConstant) -
              (1 / 2) *
                Math.asinh(
                  (adjustedSupportHeight2 - adjustedSupportHeight1) /
                    (2 * catenaryConstant * Math.sinh(spanLength / (2 * catenaryConstant)))
                )
          ),
          2
        );
  }
  /* c8 ignore stop */
  return lowPoint;
}

function calcLowestArrayItem(list) {
  let lowest;
  for (let i = 0; i < list.length; i++) {
    if (typeof lowest === 'undefined') lowest = list[i];
    else if (list[i] < lowest) {
      lowest = list[i];
    }
  }
  return lowest;
}

function calcDF1(catenaryConstant, spanLength, xDistance, adjustedSupportHeight1, adjustedSupportHeight2) {
  return (
    2 *
      (Math.pow(
        Math.sinh(
          spanLength / (4 * catenaryConstant) -
            xDistance / (2 * catenaryConstant) -
            (1 / 2) *
              Math.asinh(
                (adjustedSupportHeight2 - adjustedSupportHeight1) / (2 * catenaryConstant * Math.sinh(spanLength / (2 * catenaryConstant)))
              )
        ),
        2
      ) -
        Math.pow(
          Math.sinh(
            spanLength / (4 * catenaryConstant) -
              (1 / 2) *
                Math.asinh(
                  (adjustedSupportHeight2 - adjustedSupportHeight1) /
                    (2 * catenaryConstant * Math.sinh(spanLength / (2 * catenaryConstant)))
                )
          ),
          2
        )) +
    2 *
      catenaryConstant *
      (2 *
        Math.sinh(
          spanLength / (4 * catenaryConstant) -
            xDistance / (2 * catenaryConstant) -
            (1 / 2) *
              Math.asinh(
                (adjustedSupportHeight2 - adjustedSupportHeight1) / (2 * catenaryConstant * Math.sinh(spanLength / (2 * catenaryConstant)))
              )
        ) *
        Math.cosh(
          spanLength / (4 * catenaryConstant) -
            xDistance / (2 * catenaryConstant) -
            (1 / 2) *
              Math.asinh(
                (adjustedSupportHeight2 - adjustedSupportHeight1) / (2 * catenaryConstant * Math.sinh(spanLength / (2 * catenaryConstant)))
              )
        ) *
        (-spanLength / (4 * Math.pow(catenaryConstant, 2)) +
          xDistance / (2 * Math.pow(catenaryConstant, 2)) +
          (((1 /
            (2 *
              Math.sqrt(
                1 +
                  Math.pow(
                    (adjustedSupportHeight2 - adjustedSupportHeight1) /
                      (2 * catenaryConstant * Math.sinh(spanLength / (2 * catenaryConstant))),
                    2
                  )
              ))) *
            (adjustedSupportHeight2 - adjustedSupportHeight1)) /
            Math.pow(2 * catenaryConstant * Math.sinh(spanLength / (2 * catenaryConstant)), 2)) *
            (2 * Math.sinh(spanLength / (2 * catenaryConstant)) +
              (2 * catenaryConstant * Math.cosh(spanLength / (2 * catenaryConstant)) * -spanLength) /
                (2 * Math.pow(catenaryConstant, 2)))) -
        2 *
          Math.sinh(
            spanLength / (4 * catenaryConstant) -
              (1 / 2) *
                Math.asinh(
                  (adjustedSupportHeight2 - adjustedSupportHeight1) /
                    (2 * catenaryConstant * Math.sinh(spanLength / (2 * catenaryConstant)))
                )
          ) *
          Math.cosh(
            spanLength / (4 * catenaryConstant) -
              (1 / 2) *
                Math.asinh(
                  (adjustedSupportHeight2 - adjustedSupportHeight1) /
                    (2 * catenaryConstant * Math.sinh(spanLength / (2 * catenaryConstant)))
                )
          ) *
          (-spanLength / (4 * Math.pow(catenaryConstant, 2)) +
            (1 /
              (2 *
                Math.sqrt(
                  1 +
                    Math.pow(
                      (adjustedSupportHeight2 - adjustedSupportHeight1) /
                        (2 * catenaryConstant * Math.sinh(spanLength / (2 * catenaryConstant))),
                      2
                    )
                ))) *
              ((adjustedSupportHeight2 - adjustedSupportHeight1) /
                Math.pow(2 * catenaryConstant * Math.sinh(spanLength / (2 * catenaryConstant)), 2)) *
              (2 * Math.sinh(spanLength / (2 * catenaryConstant)) +
                (2 * catenaryConstant * Math.cosh(spanLength / (2 * catenaryConstant)) * -spanLength) /
                  (2 * Math.pow(catenaryConstant, 2)))))
  );
}

function calcDF2(
  catenaryConstant,
  spanLength,
  adjustedSupportHeight1,
  adjustedSupportHeight2,
  finalCableWeight,
  crossSectionalArea,
  modulusOfElasticity,
  finalUnstressedConductorLength
) {
  return (
    (8 * catenaryConstant * Math.pow(Math.sinh(spanLength / (2 * catenaryConstant)), 2) -
      4 * spanLength * Math.sinh(spanLength / (2 * catenaryConstant)) * Math.cosh(spanLength / (2 * catenaryConstant))) /
      (2 *
        Math.sqrt(
          4 * Math.pow(catenaryConstant, 2) * Math.pow(Math.sinh(spanLength / (2 * catenaryConstant)), 2) +
            Math.pow(adjustedSupportHeight2 - adjustedSupportHeight1, 2)
        )) -
    (finalUnstressedConductorLength * finalCableWeight) / (crossSectionalArea * modulusOfElasticity)
  );
}

function calcHorizontalTension(catenaryConstant, weight) {
  let tension;
  if (typeof catenaryConstant === 'object') {
    /* c8 ignore start */ // This block is never reached because catenaryConstant is always an object
    let tensions = [];
    for (let i = 0; i < weight.length; i++) {
      tensions[i] = catenaryConstant[i] * weight[i];
    }
    tension = tensions;
  } else {
    /* c8 ignore stop */
    tension = catenaryConstant * weight;
  }
  return tension;
}

function calcInitialCatenaryConstant(spanLength, xDistance, adjustedSupportHeight1, adjustedSupportHeight2, adjustedMidspanHeight) {
  // use Newton-Raphson Method to numerically solve for the catenary constant
  let catenaryConstantGuess = { constant: spanLength, converge: true };
  let previousCatenaryConstantGuess = spanLength;
  let epilson = 0.000001;
  let difference = 1000;
  let n = 0;
  while (difference > epilson) {
    if (n > 20) {
      difference = 0;
      catenaryConstantGuess.converge = false;
    } else {
      let f =
        calcCatenaryPosition(catenaryConstantGuess.constant, spanLength, xDistance, adjustedSupportHeight1, adjustedSupportHeight2) -
        adjustedMidspanHeight;
      let df = calcDF1(catenaryConstantGuess.constant, spanLength, xDistance, adjustedSupportHeight1, adjustedSupportHeight2);
      previousCatenaryConstantGuess = catenaryConstantGuess.constant;
      catenaryConstantGuess.constant -= f / df;
      difference = Math.abs(catenaryConstantGuess.constant - previousCatenaryConstantGuess);
      n++;
    }
  }
  return catenaryConstantGuess;
}

function calcFinalCatenaryConstant(
  spanLength,
  adjustedSupportHeight1,
  adjustedSupportHeight2,
  finalCableWeight,
  crossSectionalArea,
  modulusOfElasticity,
  finalUnstressedConductorLength
) {
  // use Newton-Raphson Method to numerically solve for the catenary constant
  let catenaryConstants = [];
  let converge = true;
  for (let i = 0; i < finalCableWeight.length; i++) {
    let catenaryConstantGuess = spanLength;
    let previousCatenaryConstantGuess = spanLength;
    let epilson = 0.000001;
    let difference = 1000;
    let n = 0;
    while (difference > epilson) {
      if (n > 20) {
        difference = 0;
        converge = false;
      } else {
        let f =
          calcConductorLength(catenaryConstantGuess, spanLength, adjustedSupportHeight1, adjustedSupportHeight2) -
          finalUnstressedConductorLength[i] *
            (1 + (catenaryConstantGuess * finalCableWeight[i]) / (crossSectionalArea * modulusOfElasticity));
        let df = calcDF2(
          catenaryConstantGuess,
          spanLength,
          adjustedSupportHeight1,
          adjustedSupportHeight2,
          finalCableWeight[i],
          crossSectionalArea,
          modulusOfElasticity,
          finalUnstressedConductorLength[i]
        );
        previousCatenaryConstantGuess = catenaryConstantGuess;
        catenaryConstantGuess -= f / df;
        difference = Math.abs(catenaryConstantGuess - previousCatenaryConstantGuess);
        n++;
      }
    }
    catenaryConstants[i] = catenaryConstantGuess;
  }
  return { constants: catenaryConstants, converge: converge };
}

function calcFinalConductorLowPoint(lowPoints, midspanElevation) {
  let lowPoint = calcLowestArrayItem(lowPoints);
  lowPoint -= midspanElevation;
  return lowPoint;
}

export function CalcPhotoDistanceRatios(connectionId, sectionId, nodes, connections) {
  if (nodes && connections && connectionId && sectionId) {
    let ratios = {};
    let section = connections?.[connectionId]?.sections?.[sectionId] ?? '';
    let n1Id = connections[connectionId].node_id_1;
    let n2Id = connections[connectionId].node_id_2;
    // Both endpoints have to be loaded before the span can be measured
    if (nodes[n1Id] == null || nodes[n2Id] == null) return;

    let lr1 = CalcLeftRight(n1Id, nodes, connections);
    let lr2 = CalcLeftRight(n2Id, nodes, connections);
    if (lr1.connection_left == lr2.connection_right || lr2.connection_left == lr1.connection_right) {
      if (lr1.connection_right == connectionId) {
        [n1Id, n2Id] = [n2Id, n1Id];
      }
    }
    let n1 = nodes[n1Id];
    let n2 = nodes[n2Id];
    let n1PhotoId = GetMainPhoto(nodes?.[n1Id]?.photos ?? '');
    let n2PhotoId = GetMainPhoto(nodes?.[n2Id]?.photos ?? '');
    let leftLocation = new GoogleGeometry.maps.LatLng(n1.latitude, n1.longitude);
    let rightLocation = new GoogleGeometry.maps.LatLng(n2.latitude, n2.longitude);
    let totalDistance = GoogleGeometry.maps.geometry.spherical.computeLength([rightLocation, leftLocation]);
    let sectionLocation = new GoogleGeometry.maps.LatLng(section.latitude, section.longitude);
    ratios[n1PhotoId] = 1 - GoogleGeometry.maps.geometry.spherical.computeLength([sectionLocation, leftLocation]) / totalDistance;
    ratios[n2PhotoId] = 1 - GoogleGeometry.maps.geometry.spherical.computeLength([sectionLocation, rightLocation]) / totalDistance;
    return ratios;
  }
}
